import type {
  TableColumn,
  TableProps,
  TableRowData,
} from '-/Custom/Table.types';
import { useEffect, useState } from 'react';
import { Pressable, ScrollView, View, type ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import HoverTooltip from '@/components/UI/HoverTooltip';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import hapticsOnPress from '@/utils/hapticsOnPress';
import sortTable from '@/utils/sortTable';

type HeaderCellSizeOptions = {
  flex?: number;
  width?: number;
};
type CellSizeOptions = {
  width?: number;
};
type SortedCellStyleOptions = {
  isSorted: boolean;
};
type RowStyleOptions = {
  alignment?: ViewStyle['justifyContent'];
  tone: 'even' | 'odd';
};
const style = StyleSheet.create((theme) => ({
  content: {
    flexGrow: 1,
    flexDirection: 'column',
  },
  headerRow: {
    backgroundColor: theme.colors.tableRowEven,
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  headerCell: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: theme.size(10),
    paddingVertical: theme.space(2),
    position: 'relative',
  },
  headerCellSize: ({ flex, width }: HeaderCellSizeOptions) => {
    if (flex) {
      return {
        alignItems: 'flex-start',
        flex,
        width,
      };
    }
    return {
      flexShrink: 0,
      width,
    };
  },
  headerButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: theme.size(8),
    overflow: 'hidden',
    width: '100%',
  },
  row: ({ alignment = 'flex-start', tone }: RowStyleOptions) => ({
    backgroundColor:
      tone === 'even' ? theme.colors.tableRowEven : theme.colors.tableRowOdd,
    flexDirection: 'row',
    justifyContent: alignment,
  }),
  cell: ({ width }: CellSizeOptions) => ({
    justifyContent: 'center',
    minWidth: width,
    paddingVertical: theme.space(2),
  }),
  sortedCell: ({ isSorted }: SortedCellStyleOptions) => {
    if (!isSorted) {
      return {};
    }
    return {
      backgroundColor: theme.colors.tableCellSorted,
    };
  },
  scrollableColumns: {
    flexGrow: 1,
    flexShrink: 1,
  },
}));
export default function Table({
  columns,
  sortKeys: initialSortKeys,
  rows,
  onPressRow,
  header = null,
  highlight = false,
  isNumericSort = false,
}: TableProps) {
  const [primarySort, setPrimarySort] = useState(initialSortKeys[0]);
  const primarySortKey = primarySort ? Object.keys(primarySort)[0] : '';
  const primarySortDirection = primarySort
    ? Object.values(primarySort)[0]
    : 'asc';
  const sortKeys: TableProps['sortKeys'] = [
    ...(primarySort ? [primarySort] : []),
    ...initialSortKeys.slice(1),
  ];
  useEffect(() => {
    if (!columns.find((column) => column.key === primarySortKey)) {
      setPrimarySort({
        [columns[0].key]: 'asc',
      });
    }
  }, [columns, primarySortKey]);
  const sortedRows = sortTable(rows, columns, sortKeys, isNumericSort);
  const stickyColumns = columns.filter((column) => column.sticky);
  const scrollableColumns = columns.filter((column) => !column.sticky);
  const handleSort = (key: string) => {
    const sortedColumn = columns.find((column) => column.key === key);
    if (!sortedColumn?.initialSortDirection && !sortedColumn?.sortFunction) {
      return;
    }
    if (primarySortKey === key) {
      const newSortDirection = primarySortDirection === 'asc' ? 'desc' : 'asc';
      setPrimarySort({
        [key]: newSortDirection,
      });
      return;
    }
    if (sortedColumn) {
      const sortDirection = sortedColumn?.initialSortDirection || 'desc';
      setPrimarySort({
        [sortedColumn.key]: sortDirection,
      });
    }
  };
  const renderCell = (rowData: TableRowData, column: TableColumn) => {
    if (column.renderCell) {
      return column.renderCell(rowData);
    }
    return (
      <Text
        textAlign="center"
        fontWeight={column.key === primarySortKey ? 'bold' : 'normal'}
        testID="TableCell"
      >
        {rowData[column.key] || 0}
      </Text>
    );
  };
  const renderHeaderCell = (column: TableColumn) => {
    return (
      <HoverTooltip
        key={column.key}
        label={column.tooltip}
        style={[
          style.headerCell,
          column.headerCellStyle,
          style.headerCellSize({
            flex: column.flex,
            width: column.width,
          }),
        ]}
      >
        <Box flex>
          <Pressable
            key={column.key}
            testID="TableHeaderCell"
            style={style.headerButton}
            onPress={hapticsOnPress(() => handleSort(column.key))}
          >
            {column.title ? (
              <Text
                textAlign="center"
                fontWeight={column.key === primarySortKey ? 'bold' : 'normal'}
              >
                {column.title}
              </Text>
            ) : null}

            {primarySortKey === column.key && (
              <Icon
                name={
                  primarySortDirection === 'asc'
                    ? 'arrow-up-outline'
                    : 'arrow-down-outline'
                }
                size="xs"
              />
            )}
          </Pressable>
        </Box>
      </HoverTooltip>
    );
  };
  const renderRow = (
    tableColumns: TableColumn[],
    rowData: TableRowData,
    index: number,
    alignment: ViewStyle['justifyContent'] | undefined = 'flex-start',
  ) => {
    return (
      <Pressable
        onPress={() => onPressRow?.(rowData)}
        key={rowData.id}
        style={style.row({
          alignment,
          tone: index % 2 ? 'even' : 'odd',
        })}
        testID="TableRow"
      >
        {tableColumns.map((column) => (
          <View
            style={[
              style.cell({
                width: column.width,
              }),
              column.cellStyle,
              style.sortedCell({
                isSorted: highlight && column.key === primarySortKey,
              }),
            ]}
            key={`${column.key}-${rowData.id}`}
          >
            {renderCell(rowData, column)}
          </View>
        ))}
      </Pressable>
    );
  };
  return (
    <>
      {header}
      <Box direction="horizontal">
        {stickyColumns.length ? (
          <View>
            <View style={style.headerRow}>
              {stickyColumns.map(renderHeaderCell)}
            </View>
            {sortedRows.map((item, index) =>
              renderRow(stickyColumns, item, index, 'flex-end'),
            )}
          </View>
        ) : null}

        <ScrollView
          nestedScrollEnabled
          horizontal
          style={style.scrollableColumns}
          contentContainerStyle={style.content}
          bounces={false}
        >
          <View style={style.headerRow}>
            {scrollableColumns.map(renderHeaderCell)}
          </View>
          {sortedRows.map((item, index) =>
            renderRow(scrollableColumns, item, index),
          )}
        </ScrollView>
      </Box>
    </>
  );
}
